/**
 * Order — сумма пополнения рублёвого счёта (шаг Flow step="order"). Ввод только в рублях,
 * конвертации/курса нет (счёт рублёвый; курс фиксируется только на выводе). Валидация по
 * лимитам из настроек. Заявка создаётся по рублям. Пропсы: onNext(order), onBack.
 * Кнопка действия — нативная MainButton (мессенджер) + FallbackButton (веб).
 */
import { useCallback, useEffect, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Note } from "@/components/Note";
import { Field } from "@/components/Field";
import { friendlyError, ordersApi, type ApiOrder } from "@/lib/api";
import { formatRub } from "@/lib/format";

type Props = {
  onNext: (order: ApiOrder) => void;
  onBack: () => void;
};

const DEFAULT_MIN_RUB = 100;
const DEFAULT_MAX_RUB = 1_000_000;

export function Order({ onNext, onBack }: Props) {
  const [minRub, setMinRub] = useState(DEFAULT_MIN_RUB);
  const [maxRub, setMaxRub] = useState(DEFAULT_MAX_RUB);
  const [rub, setRub] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    ordersApi
      .limits()
      .then((l) => {
        if (alive) {
          setMinRub(l.min_rub);
          setMaxRub(l.max_rub);
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const onRub = (raw: string) => setRub(raw.replace(/\D/g, ""));

  const rubNum = Number(rub);
  const valid = rubNum >= minRub && rubNum <= maxRub;

  const err =
    rub && rubNum < minRub
      ? `Минимум ${formatRub(minRub)}`
      : rubNum > maxRub
      ? `Максимум ${formatRub(maxRub)}`
      : undefined;

  const submit = useCallback(async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const order = await ordersApi.create(rubNum);
      onNext(order);
    } catch (e) {
      setError(friendlyError(e, "Не удалось создать заявку"));
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, rubNum, onNext]);

  useBackButton(onBack);
  useMainButton({
    text: submitting ? "Создаём заявку..." : "Пополнить счёт",
    onClick: submit,
    active: valid,
    loading: submitting,
  });

  const rubDisplay = rub ? Number(rub).toLocaleString("ru-RU") : "";

  return (
    <Screen>
      <Hero icon="🪙" title="Пополнение счёта" subtitle="Введите сумму в рублях" />

      <Field
        label="Сумма пополнения"
        type="text"
        inputMode="numeric"
        autoFocus
        placeholder="0"
        value={rubDisplay}
        onChange={(e) => onRub(e.target.value)}
        suffix={<span className="text-[14px] text-white/45">₽</span>}
        error={err}
        hint={!err ? `от ${formatRub(minRub)} до ${formatRub(maxRub)}` : undefined}
      />

      <Note className="mt-4">Реквизиты для оплаты появятся после создания заявки. Счёт пополняется в рублях.</Note>

      {error && (
        <Note variant="danger" className="mt-4">
          {error}
        </Note>
      )}

      <FallbackButton text="Пополнить счёт" onClick={submit} disabled={!valid} loading={submitting} />
    </Screen>
  );
}
